import React, { useEffect, useState } from "react";
import { api } from "@/api";
import { cld } from "@/lib/cld";
import { useTranslation } from "react-i18next";
import { Save, Plus, Trash2, Star, X, Upload } from "lucide-react";

const LANGS = ["fr", "en", "de", "zh-CN"];

function I18nInput({ value, onChange, label, textarea = false }) {
  const v = value || {};
  return (
    <div>
      <label className="overline mb-2 block">{label}</label>
      <div className="space-y-2">
        {LANGS.map(l => (
          <div key={l} className="flex gap-2 items-start">
            <span className="text-[10px] tracking-[0.2em] uppercase text-[#D4AF37] w-12 pt-3">{l}</span>
            {textarea ? <textarea rows={3} value={v[l] || ""} onChange={e => onChange({ ...v, [l]: e.target.value })} className="input-luxe flex-1" /> : <input value={v[l] || ""} onChange={e => onChange({ ...v, [l]: e.target.value })} className="input-luxe flex-1" />}
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminReviews() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/reviews").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (editing.id) await api.put(`/reviews/${editing.id}`, editing);
    else await api.post("/reviews", editing);
    setEditing(null); load();
  };
  const remove = async (id) => { if (confirm("Supprimer ?")) { await api.delete(`/reviews/${id}`); load(); } };

  return (
    <div className="p-8 lg:p-12" data-testid="admin-reviews-page">
      <div className="overline">Back-office</div>
      <h1 className="font-serif-luxe text-4xl text-[#F8F5EF] mt-2 mb-8">{t("admin.reviews")}</h1>
      <button onClick={() => setEditing({ author: "", rating: 5, comment: { fr: "" }, source: "Site" })} className="btn-gold mb-6" data-testid="admin-new-review"><Plus size={14} /> Nouvel avis</button>
      <div className="luxe-card divide-y divide-[#D4AF37]/10">
        {rows.length === 0 && <div className="p-8 text-center text-[#F8F5EF]/50">—</div>}
        {rows.map(r => (
          <div key={r.id} className="p-4 flex items-center gap-4">
            <div className="flex gap-0.5">{Array.from({ length: r.rating }).map((_, i) => <Star key={i} size={12} className="fill-[#D4AF37] text-[#D4AF37]" />)}</div>
            <div className="flex-1">
              <div className="text-sm text-[#F8F5EF]">{r.author} · <span className="text-[#D4AF37] text-xs uppercase tracking-[0.2em]">{r.source}</span></div>
              <div className="text-xs text-[#F8F5EF]/60 line-clamp-1">{r.comment?.fr}</div>
            </div>
            <button onClick={() => setEditing(r)} className="btn-ghost-gold text-[10px]">Modifier</button>
            <button onClick={() => remove(r.id)} className="text-red-400"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      {editing && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-[#0A0A0A] border border-[#D4AF37]/30 max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8 relative">
            <button onClick={() => setEditing(null)} className="absolute top-4 right-4 text-[#D4AF37]"><X size={20} /></button>
            <div className="space-y-5">
              <div><label className="overline mb-2 block">Auteur</label><input value={editing.author} onChange={e => setEditing({ ...editing, author: e.target.value })} className="input-luxe" /></div>
              <div><label className="overline mb-2 block">Note (1-5)</label><input type="number" min="1" max="5" value={editing.rating} onChange={e => setEditing({ ...editing, rating: parseInt(e.target.value) })} className="input-luxe" /></div>
              <div><label className="overline mb-2 block">Source</label><input value={editing.source} onChange={e => setEditing({ ...editing, source: e.target.value })} className="input-luxe" /></div>
              <I18nInput label="Commentaire" value={editing.comment} onChange={(v) => setEditing({ ...editing, comment: v })} textarea />
              <button onClick={save} className="btn-gold w-full justify-center" data-testid="admin-save-review"><Save size={14} /> Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminBlog() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/blog").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (editing.id) await api.put(`/blog/${editing.id}`, editing);
    else await api.post("/blog", editing);
    setEditing(null); load();
  };
  const remove = async (id) => { if (confirm("Supprimer ?")) { await api.delete(`/blog/${id}`); load(); } };

  return (
    <div className="p-8 lg:p-12" data-testid="admin-blog-page">
      <div className="overline">Back-office</div>
      <h1 className="font-serif-luxe text-4xl text-[#F8F5EF] mt-2 mb-8">{t("admin.blog")}</h1>
      <button onClick={() => setEditing({ title: { fr: "" }, slug: "", excerpt: { fr: "" }, content: { fr: "" }, cover_url: "", published: true })} className="btn-gold mb-6" data-testid="admin-new-blog"><Plus size={14} /> Nouvel article</button>
      <div className="luxe-card divide-y divide-[#D4AF37]/10">
        {rows.length === 0 && <div className="p-8 text-center text-[#F8F5EF]/50">—</div>}
        {rows.map(p => (
          <div key={p.id} className="p-4 flex items-center gap-4">
            <div className="flex-1">
              <div className="font-serif-luxe text-lg text-[#F8F5EF]">{p.title?.fr}</div>
              <div className="text-xs text-[#F8F5EF]/60">/{p.slug}</div>
            </div>
            <button onClick={() => setEditing(p)} className="btn-ghost-gold text-[10px]">Modifier</button>
            <button onClick={() => remove(p.id)} className="text-red-400"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      {editing && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-[#0A0A0A] border border-[#D4AF37]/30 max-w-3xl w-full max-h-[90vh] overflow-y-auto p-8 relative">
            <button onClick={() => setEditing(null)} className="absolute top-4 right-4 text-[#D4AF37]"><X size={20} /></button>
            <div className="space-y-5">
              <I18nInput label="Titre" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} />
              <div><label className="overline mb-2 block">Slug (url)</label><input value={editing.slug} onChange={e => setEditing({ ...editing, slug: e.target.value })} className="input-luxe" placeholder="mon-article" /></div>
              <I18nInput label="Extrait" value={editing.excerpt} onChange={(v) => setEditing({ ...editing, excerpt: v })} textarea />
              <I18nInput label="Contenu" value={editing.content} onChange={(v) => setEditing({ ...editing, content: v })} textarea />
              <div><label className="overline mb-2 block">URL image de couverture</label><input value={editing.cover_url} onChange={e => setEditing({ ...editing, cover_url: e.target.value })} className="input-luxe" placeholder="https://..." /></div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.published} onChange={e => setEditing({ ...editing, published: e.target.checked })} /><span>Publié</span></label>
              <button onClick={save} className="btn-gold w-full justify-center" data-testid="admin-save-blog"><Save size={14} /> Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminGallery() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [pendingMeta, setPendingMeta] = useState(null);
  const [urlForm, setUrlForm] = useState({ url: "", category: "plats", type: "photo" });
  const [uploadError, setUploadError] = useState("");

  const load = () => api.get("/gallery").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const handleUpload = async (e, category) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setUploadError("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const isVideo = file.type.startsWith("video/");
      await api.post("/gallery", {
        type: isVideo ? "video" : "photo",
        url: data.url,
        category,
        alt: { fr: file.name },
        title: { fr: file.name },
      });
      load();
    } catch (err) {
      setUploadError("Erreur upload: " + (err.response?.data?.detail || err.message));
    } finally { setUploading(false); e.target.value = ""; }
  };

  const addByUrl = async () => {
    if (!urlForm.url) return;
    await api.post("/gallery", { ...urlForm, alt: { fr: "" }, title: { fr: "" } });
    setUrlForm({ url: "", category: urlForm.category, type: urlForm.type });
    load();
  };

  const remove = async (id) => { if (confirm("Supprimer ?")) { await api.delete(`/gallery/${id}`); load(); } };

  const cats = ["plats", "salle", "ambiance", "equipe", "evenements"];

  return (
    <div className="p-8 lg:p-12" data-testid="admin-gallery-page">
      <div className="overline">Back-office</div>
      <h1 className="font-serif-luxe text-4xl text-[#F8F5EF] mt-2 mb-8">{t("admin.gallery")}</h1>

      <div className="overline mb-3">Téléverser un fichier</div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
        {cats.map(c => (
          <label key={c} className="luxe-card p-5 cursor-pointer hover:border-[#D4AF37]/50 text-center" data-testid={`upload-${c}`}>
            <Upload size={20} className="text-[#D4AF37] mx-auto mb-2" />
            <div className="text-[10px] tracking-[0.22em] uppercase text-[#F8F5EF]">{c}</div>
            <input type="file" accept="image/*,video/*" className="hidden" onChange={(e) => handleUpload(e, c)} />
          </label>
        ))}
      </div>
      {uploading && <div className="text-[#D4AF37] text-sm mb-2">Téléversement en cours…</div>}
      {uploadError && <div className="text-amber-400 text-sm mb-4">{uploadError}</div>}

      <div className="overline mb-3 mt-6">Ou ajouter par URL externe</div>
      <div className="luxe-card p-5 mb-8 flex flex-col md:flex-row gap-3" data-testid="gallery-url-form">
        <input
          placeholder="https://images.unsplash.com/…"
          value={urlForm.url}
          onChange={e => setUrlForm({ ...urlForm, url: e.target.value })}
          className="input-luxe flex-1"
          data-testid="gallery-url-input"
        />
        <select value={urlForm.category} onChange={e => setUrlForm({ ...urlForm, category: e.target.value })} className="input-luxe md:w-44">
          {cats.map(c => <option key={c} value={c} className="bg-[#1A1A1A]">{c}</option>)}
        </select>
        <select value={urlForm.type} onChange={e => setUrlForm({ ...urlForm, type: e.target.value })} className="input-luxe md:w-36">
          <option value="photo" className="bg-[#1A1A1A]">photo</option>
          <option value="video" className="bg-[#1A1A1A]">video</option>
        </select>
        <button onClick={addByUrl} className="btn-gold" data-testid="gallery-url-add"><Plus size={14} /> Ajouter</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {items.map(it => {
          const raw = it.url?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${it.url}` : it.url;
          const url = cld(raw, "thumb");
          return (
            <div key={it.id} className="luxe-card overflow-hidden group relative">
              {it.type === "video" ? <video src={url} muted className="w-full h-40 object-cover" /> : <img loading="lazy" src={url} alt="" className="w-full h-40 object-cover" />}
              <div className="p-2 flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">{it.category}</span>
                <button onClick={() => remove(it.id)} className="text-red-400 hover:text-red-300"><Trash2 size={14} /></button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
